import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui-kit/avatar';
import { Button } from '../ui-kit/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui-kit/popover';

import { useActiveOrganization, useMyOrganizations } from '@/graphql/queries/organizations';
import { useSwitchOrg } from '@/api-client/use-swich-organization';
import { useQueryClient } from '@tanstack/react-query';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { orgPath } from '@/modules/org/utils/org-path';
import { SYSTEM_ADMIN_ORG_ID, type MyOrganization } from '@/graphql/queries/organizations';
import { useAuthStore } from '@/state/store/auth';
import { useMyOgnizationStore } from '@/store';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const ROLE_LABELS: Record<string, string> = {
  org_admin: 'Admin',
  org_editor: 'Editor',
  org_member: 'Member',
};

interface OrgSwitcherProps {
  onSelect?: () => void;
}

export function OrgSwitcher({ onSelect }: OrgSwitcherProps = {}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const orgId = useAuthStore((state) => state.orgId);
  const currentIamOrg = useMyOgnizationStore((state) => state.currentOrganization);
  const { data: activeOrgFromDb } = useActiveOrganization();
  const { data: dbOrgs, isLoading } = useMyOrganizations();
  const { mutateAsync: switchOrganizationAsync, isPending } = useSwitchOrg();
  const { handleError } = useErrorHandler();
  const queryClient = useQueryClient();

  const orgs = dbOrgs?.blocksOrgs ?? [];
  const activeOrg =
    orgs.find((o) => o.BlocksOrgId === orgId) ??
    orgs.find((o) => o.BlocksOrgId === currentIamOrg?.itemId) ??
    activeOrgFromDb ??
    null;

  const getRoleForOrg = (blocksOrgId: string) => {
    const membership = user?.memberships?.find((m) => m.organizationId === blocksOrgId);
    const role = membership?.roles?.[0];
    return role ? (ROLE_LABELS[role] ?? role) : null;
  };

  const handleSelect = async (org: MyOrganization) => {
    if (isPending) return;
    try {
      await switchOrganizationAsync({
        organization_id: org.BlocksOrgId,
        organization_name: org.Name,
      });
      useAuthStore
        .getState()
        .setOrg(org.BlocksOrgId, org.Slug || org.BlocksOrgId, org.ItemId, org.SigOrgId);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate(org.BlocksOrgId === SYSTEM_ADMIN_ORG_ID ? '/admin' : orgPath(org.BlocksOrgId));
      setOpen(false);
      onSelect?.();
    } catch (error) {
      handleError(error, { defaultMessage: 'Failed to switch organization' });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="primaryOutline" size="sm" className="rounded-full gap-2">
          {activeOrg ? (
            <>
              <Avatar className="h-6 w-6">
                {activeOrg.LogoUrl && <AvatarImage src={activeOrg.LogoUrl} alt={activeOrg.Name} />}
                <AvatarFallback className="bg-selise-blue-500 text-white h-6 w-6 rounded-full flex items-center justify-center text-sm font-semibold">
                  {activeOrg.Name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className="max-w-32 truncate">{activeOrg.Name}</span>
            </>
          ) : (
            <span>{t('SELECT_ORGANIZATION', 'Select Org')}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <div className="text-xs font-medium text-globe-grey px-2 py-1.5">
          {t('COMMON_ORGANIZATIONS')}
        </div>
        <div className="max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="px-2 py-3 text-sm text-globe-grey">Loading...</div>
          ) : (
            orgs?.map((org) => (
              <button
                key={org.BlocksOrgId}
                type="button"
                onClick={() => handleSelect(org)}
                disabled={isPending}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-60"
              >
                <Avatar className="h-7 w-7 shrink-0">
                  {org.LogoUrl && <AvatarImage src={org.LogoUrl} alt={org.Name} />}
                  <AvatarFallback className="bg-selise-blue-500 text-white text-xs font-semibold">
                    {org.Name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <span className="block truncate">{org.Name}</span>
                  {getRoleForOrg(org.BlocksOrgId) && (
                    <span className="block text-xs text-globe-grey">
                      {getRoleForOrg(org.BlocksOrgId)}
                    </span>
                  )}
                </div>
                {activeOrg?.BlocksOrgId === org.BlocksOrgId && (
                  <Check className="h-4 w-4 text-selise-blue" />
                )}
              </button>
            ))
          )}
        </div>
        <div className="border-t border-border mt-1 pt-1">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/org/new');
              onSelect?.();
            }}
            className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted transition-colors"
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-border">
              <Plus className="h-4 w-4 text-globe-grey" />
            </div>
            <span>{t('COMMON_CREATEORGANIZATION')}</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
